import { useIonAlert } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CommunityModelFactory } from '../share-caremap/CommunityModelFactory';

// Shared Save wrapper for the member edit forms (household, support-group, patient). When the
// email being saved would newly link the member to an existing Innerhive account — the member
// is not yet linked and the email matches the owner or an accepted collaborator, the same rule
// the server auto-links on — it first asks the user to confirm. Confirm (Connect & Save) → save;
// Cancel → stay on the form. Otherwise it saves straight through with no interruption.
//
// The predicate + copy live here so all three forms stay in sync (see CLAUDE.md: drawer-hosted
// entities have multiple edit components). The CommunityModel for the active caremap is loaded
// eagerly by CommunityModelFactory as soon as the caremap becomes active, so by the time a form
// is open and saved it is loaded; if it somehow is not, we can't know whether the email links, so
// we save straight through (the server still links) rather than block on a warning we can't form.
export const useConfirmLinkThenSave = () => {
    const [presentAlert] = useIonAlert();
    const factory = useInstance(CommunityModelFactory);

    return ({ linkedUserId, email, name, isSelf = false, memberNoun = 'member', save }: {
        linkedUserId?: string;
        email: string;
        name?: string;
        // The linked account is the current user's own (owner self-link) — use second-person copy,
        // naming the node by its type (memberNoun) rather than by the person's name.
        isSelf?: boolean;
        memberNoun?: string;
        save: () => void;
    }) => {
        const community = factory.forActiveCaremap();
        const wouldLink = !linkedUserId && community.isLoaded() && community.emailWouldLink(email);

        if (!wouldLink) {
            save();
            return;
        }

        presentAlert({
            header: isSelf ? 'Connect to your account?' : 'Connect to their account?',
            message: isSelf
                ? `Saving this email connects this ${memberNoun} to your Innerhive account. Anything you enter here updates your name, photo, and contact info everywhere you appear — but blank fields won't erase what's already on your account.`
                : `Saving this email connects ${name || 'this person'} to their existing Innerhive account. Anything you enter here updates their name, photo, and contact info everywhere they appear — but blank fields won't erase what's already on their account.`,
            cssClass: 'drawer-alert',
            buttons: [
                { text: 'Cancel', role: 'cancel' },
                { text: 'Connect & Save', handler: () => save() }
            ]
        });
    };
};
